import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Button,
    LoaderPage,
    RegionState,
    useAuthentication,
    useI18n,
    useSupport,
    useTracking,
} from '@freelancelabs/components';
import { AppName, consommerSortie, marquerSortie, redirectToApp } from '@freelancelabs/shared';

import { useMfaGate } from '../domains/shared/hooks/useMfaGate';
import useViewerAttachment from '../domains/shared/hooks/useViewerAttachment';
import supportEvents from '../events/support';
import trackEvents from '../events/tracking';
import { ViewerDocument } from '../graphql/__generated__/types';
import AppRoutesTree from '../routes';

const Setup = () => {
    const { isInitialized: isInitializedAuth, hasSession, logout } = useAuthentication();
    const { isInitialized: isInitializedI18n } = useI18n();
    const { data } = useQuery(ViewerDocument);

    const viewer = data?.viewer;
    // A user can be attached to several accounts. The API answers on the default
    // one, so that is the membership whose role describes the current session.
    const membership = viewer?.memberships?.find(({ isDefault }) => isDefault) ?? viewer?.memberships?.[0];

    // The Client Hub schema knows nothing of the Connecteed role nor of a job
    // title: sending fewer fields to Mixpanel and Intercom beats sending wrong ones.
    const userData = {
        id: viewer?.id ?? undefined,
        email: viewer?.email ?? undefined,
        firstName: viewer?.firstName,
        lastName: viewer?.lastName,
        customerPortalRole: membership?.role?.name,
    };
    useTracking(trackEvents, userData);
    useSupport(supportEvents, userData);
    const mfa = useMfaGate(viewer?.mfaRequired);
    const attachment = useViewerAttachment();

    /**
     * SANS AUCUN RATTACHEMENT, LA PERSONNE EST DECONNECTEE ET RENVOYEE A LA CONNEXION,
     * qui lui dit pourquoi. Decide par Mohamed le 2026-08-29.
     *
     * **C'est un renversement de PCG-118, et il faut le lire avant de le defaire.** Ce
     * lot-la refusait une garde a la racine, et son argument principal etait qu'une garde
     * emporte la barre laterale, donc « Se deconnecter » avec elle, et laisse la personne
     * dans un cul-de-sac. Envoyer a la connexion REPOND a cet argument : ce n'est pas un
     * mur, c'est une sortie. L'autre argument de PCG-118 tient toujours, et c'est lui qui
     * dicte la forme ci-dessous.
     *
     * **ON NE DECONNECTE QUE QUAND ON SAIT.** `useViewerAttachment` rend quatre etats, et
     * un seul autorise ce geste : `unattached`, ou l'API a repondu qu'il n'y a aucun
     * rattachement. En `loading` on ne sait pas encore ; en `failed` la requete a echoue,
     * et deconnecter la-dessus mettrait quelqu'un dehors pour une coupure reseau, sans
     * qu'il comprenne, et sans qu'aucun journal ne le distingue d'un refus legitime.
     * C'est le sens d'echec que PCG-118 reprochait a une garde : il est choisi ici, et
     * il est choisi OUVERT.
     *
     * **L'adresse courante ne voyage PAS.** Partout ailleurs le portail passe
     * `redirect-after-login` pour que la personne revienne ou elle allait. Ici ce serait
     * une boucle : elle se reconnecte, revient, n'a toujours aucun rattachement, et
     * ressort. Elle reste donc sur `account`, ou le message l'attend.
     *
     * **LE MESSAGE EXISTE DEJA LA-BAS ET ON NE LE REECRIT PAS.** `RedirectMiddleware`
     * fait ce meme geste depuis PCG-119 pour qui n'a pas la portee voulue : deconnecter,
     * puis renvoyer a la connexion avec `APP_UNAUTHORIZED`. En ecrire une seconde version
     * ici donnerait deux phrases pour un seul refus, libres de deriver.
     *
     * Ce qui se passe d'une application a l'autre est donc une CHAINE, et c'est le prix
     * de la frontiere : `location.state` ne la traverse pas, `Login.tsx` lit ce parametre
     * et le verifie contre son enumeration. Les deux cotes se modifient ensemble.
     */
    const sansRattachement = attachment.status === 'unattached';
    /**
     * SI LA SORTIE ECHOUE, ON NE LAISSE PERSONNE SUR UN TOURNIQUET.
     *
     * `logout()` peut lever : Amplify parle au reseau. Sans ce drapeau, l'ecran rendrait
     * `LoaderPage` pour toujours, ce qui est le pire des rendus possibles, une page qui
     * n'a pas fini de charger et ne finira jamais.
     *
     * On retombe alors sur l'accueil, qui dit deja « votre acces n'est pas configure »
     * (PCG-118). On ne redirige PAS quand meme : la session est encore ouverte, donc
     * `account` renverrait la personne ici, qui la renverrait la-bas.
     */
    const [sortieEnEchec, setSortieEnEchec] = React.useState(false);
    React.useEffect(() => {
        if (!sansRattachement || sortieEnEchec) {
            return;
        }
        // On MARQUE puis on deconnecte, et on ne redirige PAS d'ici : `logout()` met
        // `hasSession` a faux, donc la branche ci-dessous part de toute facon. Rediriger
        // ici aussi mettait deux navigations en concurrence, et c'est la seconde qui
        // gagnait : verifie a l'ecran, on atterrissait sur `?redirect-after-login=` au
        // lieu du message.
        marquerSortie('APP_UNAUTHORIZED');
        void logout().catch(() => setSortieEnEchec(true));
    }, [sansRattachement, sortieEnEchec, logout]);

    if (!isInitializedAuth || !isInitializedI18n) {
        return <LoaderPage />;
    }

    if (isInitializedAuth && isInitializedI18n && !hasSession) {
        /*
         * LE SEUL CHEMIN DE REDIRECTION VERS `account`, et il porte le motif quand une
         * garde vient de mettre quelqu'un dehors.
         *
         * L'adresse courante ne voyage alors PAS : la personne se reconnecterait, et
         * reviendrait se faire sortir. Elle reste sur la connexion, ou le message
         * l'attend.
         */
        const motif = consommerSortie();
        redirectToApp(AppName.ACCOUNT, motif ? { alerte: motif } : { 'redirect-after-login': window.location.href });
        return <LoaderPage />;
    }

    /**
     * LA BARRIERE DU MFA (PCG-303), et elle est posee ICI parce que c'est deja d'ici
     * que le portail renvoie vers `account` quand il manque une session : c'est la
     * meme forme, une condition d'entree, avec une autre condition.
     *
     * **Ce n'est PAS la garde a la racine que PCG-118 a refusee**, et la distinction
     * est ce qui autorise celle-ci. Ce refus portait sur un etat DURABLE que la
     * personne ne peut pas reparer (« aucun rattachement »), ou une garde dit « vous
     * n'avez rien a faire ici » en emportant la barre laterale, donc « Se deconnecter »
     * avec elle. Ici l'etat est TEMPORAIRE, un seul geste le leve, ce geste est sur
     * l'ecran, et la sortie est a cote : un arret, pas un cul-de-sac.
     *
     * **Elle ferme la porte que l'application de compte ne peut pas fermer** : le
     * cookie de session est pose sur `.freelance.com`, donc une personne deja
     * connectee arrive ici sans repasser par la-bas.
     *
     * **Un LIEN et non une redirection automatique.** Envoyer le navigateur vers
     * `account` tout seul se lit bien jusqu'a ce que les deux applications se
     * contredisent : le portail lit l'exigence, `account` la relit et echoue OUVERT
     * sur une erreur reseau, ce qui renvoie la personne ici, qui la renvoie la-bas.
     * Un bouton ne peut pas faire ce va-et-vient. Ca coute un clic, et ca achete une
     * boucle qui ne peut pas exister.
     *
     * `RegionState` sert ici hors d'une liste, comme la page d'accueil le fait depuis
     * PCG-118. C'est le TROISIEME emploi de ce genre, et la note de ce lot-la disait
     * qu'au troisieme il faudrait le promouvoir en etat de region : le seuil est
     * franchi, la promotion est un lot a elle.
     */
    // Le temps que la deconnexion et la redirection se fassent, on ne rend pas les
    // routes : elles rendraient leurs refus a quelqu'un qui est deja en train de partir.
    if (sansRattachement && !sortieEnEchec) {
        return <LoaderPage />;
    }

    if (mfa.blocked) {
        return (
            <main className="flex flex-1 h-full flex-col items-center justify-center gap-6 p-6">
                <RegionState
                    title={c('Mfa required').t`Two-factor authentication is required`}
                    description={c('Mfa required')
                        .t`Your organisation requires it before you use your space. Set up an authenticator app once, and a code from it will be asked for at each sign-in afterwards.`}
                >
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <Button
                            onClick={() =>
                                // L'adresse courante voyage : la personne revient ou elle
                                // allait, et non sur une page d'accueil qu'elle n'a pas
                                // demandee.
                                redirectToApp(AppName.ACCOUNT, {
                                    'redirect-after-login': window.location.href,
                                })
                            }
                        >
                            {c('Mfa required action').t`Set it up now`}
                        </Button>
                        {/* La sortie doit exister : l'alternative a poser un facteur est
                            de partir, jamais d'etre enferme. */}
                        <Button onClick={logout} category="weak">
                            {c('Action').t`Log out`}
                        </Button>
                    </div>
                </RegionState>
            </main>
        );
    }

    return <AppRoutesTree />;
};

export default Setup;
